import { type ClassValue, clsx } from "clsx";
import type { CustomerGets, IneligibleItem } from "@/types";
import type { ShippingMethod } from "./supabase-api";

export interface AppliedDiscount {
  code: string;
  amount: number; // in cents
  type: string; // DiscountType value
}

/**
 * Utility function to merge class names
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/**
 * Format cents to ZAR currency string
 * @param cents - Amount in cents
 * @returns Formatted string like "R 1,234.56"
 */
export function formatCurrency(cents: number): string {
  const amount = cents / 100;
  return `R ${amount.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Format date to readable string
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Get session token from localStorage
 */
export function getSessionToken(): string | null {
  return localStorage.getItem("session_token");
}

/**
 * Set session token in localStorage
 */
export function setSessionToken(token: string): void {
  localStorage.setItem("session_token", token);
}

/**
 * Get session ID from localStorage
 */
export function getSessionId(): string | null {
  return localStorage.getItem("session_id");
}

/**
 * Set session ID in localStorage
 */
export function setSessionId(sessionId: string): void {
  localStorage.setItem("session_id", sessionId);
}

/**
 * Clear session data from localStorage
 */
export function clearSession(): void {
  localStorage.removeItem("session_token");
  localStorage.removeItem("session_id");
}

/**
 * Get discount percentage from localStorage
 */
export function getDiscountPercentage(): number | null {
  const value = localStorage.getItem("discount_percentage");
  return value ? parseFloat(value) : null;
}

/**
 * Set discount percentage in localStorage
 */
export function setDiscountPercentage(percentage: number): void {
  localStorage.setItem("discount_percentage", String(percentage));
}

/**
 * Clear discount data from localStorage
 */
export function clearDiscount(): void {
  localStorage.removeItem("discount_percentage");
  localStorage.removeItem("discount_code");
  localStorage.removeItem("discount_total");
  localStorage.removeItem("discount_value");
  localStorage.removeItem("code_type");
  localStorage.removeItem("is_shipping_discount");
  localStorage.removeItem("is_order_discount");
  localStorage.removeItem("is_bxgy_discount");
  localStorage.removeItem("customer_gets");
  localStorage.removeItem("ineligible_items");
  localStorage.removeItem("applied_discounts");
}

/**
 * Get discount total from localStorage
 */
export function getDiscountTotal(): number | null {
  const value = localStorage.getItem("discount_total");
  return value ? parseInt(value, 10) : null;
}

/**
 * Set discount total in localStorage
 */
export function setDiscountTotal(total: number): void {
  localStorage.setItem("discount_total", String(total));
}

/**
 * Get discount code from localStorage
 */
export function getDiscountCode(): string | null {
  return localStorage.getItem("discount_code");
}

/**
 * Set discount code in localStorage
 */
export function setDiscountCode(code: string): void {
  localStorage.setItem("discount_code", code);
}

export function setIsShippingDiscountStorage(check: "TRUE" | "FALSE"){
  localStorage.setItem("is_shipping_discount", check)
}
export function getIsShippingDiscountStorage(){
  return localStorage.getItem("is_shipping_discount")
}

export function setIsOrderDiscountStorage(check: "TRUE" | "FALSE"){
  localStorage.setItem("is_order_discount", check)
}
export function getIsOrderDiscountStorage(){
  return localStorage.getItem("is_order_discount")
}

export function setIsBxgyDiscountStorage(check: "TRUE" | "FALSE"){
  localStorage.setItem("is_bxgy_discount", check)
}
export function getIsBxgyDiscountStorage(){
  return localStorage.getItem("is_bxgy_discount")
}

export function setDiscountValueStorage(value: number): void {
  localStorage.setItem("discount_value", String(value));
}
export function getDiscountValueStorage(): number | null {
  const value = localStorage.getItem("discount_value");
  return value ? parseFloat(value) : null;
}

export function setCodeTypeStorage(codeType: string): void {
  localStorage.setItem("code_type", codeType);
}
export function getCodeTypeStorage(): string | null {
  return localStorage.getItem("code_type");
}

export function setCustomerGetsStorage(customerGets: CustomerGets[] | CustomerGets): void {
  const arr = Array.isArray(customerGets) ? customerGets : [customerGets];
  localStorage.setItem("customer_gets", JSON.stringify(arr));
}
export function getCustomerGetsStorage(): CustomerGets[] | null {
  const value = localStorage.getItem("customer_gets");
  if (!value) return null;
  const parsed = JSON.parse(value);
  return Array.isArray(parsed) ? parsed : [parsed];
}

export function setIneligibleItemsStorage(items: IneligibleItem[]): void {
  localStorage.setItem("ineligible_items", JSON.stringify(items));
}

export function getIneligibleItemsStorage(): IneligibleItem[] | null {
  const value = localStorage.getItem("ineligible_items");
  if (!value) return null;
  const parsed = JSON.parse(value);
  return Array.isArray(parsed) ? parsed : [];
}

export function isIneligibleCartItem(item: { id?: string; product_id: string }): boolean {
  const ineligible = getIneligibleItemsStorage();
  if (!ineligible || ineligible.length === 0) return false;
  return ineligible.some(
    (inc) =>
      (inc.cart_id && inc.cart_id === item.id) ||
      inc.product_id === item.product_id,
  );
}

export function setAppliedDiscountsStorage(discounts: AppliedDiscount[]): void {
  localStorage.setItem("applied_discounts", JSON.stringify(discounts));
}

export function getAppliedDiscountsStorage(): AppliedDiscount[] {
  const value = localStorage.getItem("applied_discounts");
  if (!value) return [];
  const parsed = JSON.parse(value);
  return Array.isArray(parsed) ? parsed : [];
}

export interface IsFreeItemInput {
  id?: string;
  product_id: string;
  sku?: string | null;
  quantity: number;
  line_total_cents: number;
  unit_price_cents: number;
}

/**
 * Helper: check if a customerGets entry matches the item.
 * If cg.selected_variant is set (not null/undefined), item must have the same selected_variant.
 */
function cgMatchesItem(cg: CustomerGets, item: IsFreeItemInput): boolean {
  if (cg.product_id !== item.product_id) return false;
  if (cg.selected_variant != null && cg.selected_variant !== item.sku) {
    return false;
  }
  return true;
}

function CalculateDiscountTotal(item: IsFreeItemInput, discountValue: number, isFlatRate: boolean){
  const total = isFlatRate ? (item.quantity * (discountValue*100)) : (item.line_total_cents * discountValue)

  return total
}

// Compute isFreeItem prop for a cart item: true = fully free, number = discounted total cents, false = no discount
export function getIsFreeItemForOrderItem(item: IsFreeItemInput): boolean | number {
  if (isIneligibleCartItem(item)) return false;

  const customerGets = getCustomerGetsStorage();
  if (!customerGets) return false;

  const discountValue = getDiscountValueStorage() ?? 0;
  const isDiscountBasic = getIsOrderDiscountStorage() === "TRUE";

  // DiscountCodeBasic with customerGets: match by product_id + selected_variant
  if (isDiscountBasic) {
    const matchedCg = customerGets.find((cg) => cgMatchesItem(cg, item));
    if (matchedCg) {
      const isFlatRate = discountValue > 1;
      const discountAmount = CalculateDiscountTotal(item, discountValue, isFlatRate);
      const discountedTotal = item.line_total_cents - discountAmount;
      return discountedTotal <= 0 ? true : discountedTotal;
    }
    return false;
  }

  // Same product BxGY
  const sameProductCg = customerGets.find(
    (cg) => "discountedQTY" in cg && cg.product_id === item.product_id,
  );
  if (sameProductCg && "discountedQTY" in sameProductCg) {
    const unitPrice = item.quantity > 0 ? item.line_total_cents / item.quantity : item.unit_price_cents;
    const discountAmount =
      discountValue > 1
        ? Math.round(discountValue * 100 * sameProductCg.discountedQTY)
        : Math.round(sameProductCg.discountedQTY * unitPrice * discountValue);
    const discountedTotal = item.line_total_cents - discountAmount;
    return discountedTotal <= 0 ? true : discountedTotal;
  }

  // Different product BxGY
  const diffCg = customerGets.find(
    (cg) => "quantity" in cg && cgMatchesItem(cg, item),
  );
  if (diffCg) {
    if (discountValue > 1) {
      const qty = "quantity" in diffCg ? diffCg.quantity : 1;
      const discountedTotal = item.line_total_cents - Math.round(discountValue * 100 * qty);
      return discountedTotal <= 0 ? true : discountedTotal;
    }
    if (discountValue === 1) {
      return true;
    }
    const discountedTotal = Math.round(item.line_total_cents * (1 - discountValue));
    return discountedTotal <= 0 ? true : discountedTotal;
  }

  return false;
}

export function calculateDiscountedTotal(total: number): number {
  const discountValue = getDiscountValueStorage();
  const codeType = getCodeTypeStorage();

  if (discountValue === null || !codeType) return total;

  if (codeType === "DiscountPercentage") {
    return total - Math.round(total * discountValue);
  } else {
    // DiscountAmount: discountValue > 1 means flat rate in rands, convert to cents (* 100)
    const discountCents = discountValue > 1
      ? Math.round(discountValue * 100)
      : Math.round(discountValue);
    return total - discountCents;
  }
}

/**
 * Extract session token and session_id from URL params
 * Checks both query params and path params
 */
export function extractSessionFromUrl(
  search = window.location.search,
  pathname = window.location.pathname,
): { token: string | null; sessionId: string | null } | null {
  const params = new URLSearchParams(search);
  const querySessionId = params.get("session");
  const token = params.get("token");
  const pathMatch = pathname.match(/^\/(?:order|express)\/([^/]+)(?:\/|$)/);
  const encodedPathSessionId = pathMatch?.[1];
  let pathSessionId: string | null = null;
  try {
    pathSessionId = encodedPathSessionId ? decodeURIComponent(encodedPathSessionId) : null;
  } catch {
    return null;
  }

  if (querySessionId && pathSessionId && querySessionId !== pathSessionId) {
    return null;
  }

  const sessionId = querySessionId || pathSessionId;
  return sessionId || token ? { token, sessionId } : null;
}

/**
 * Validate South African phone number
 */
export function validateSAPhone(phone: string): boolean {
  // Match +27 or 0 followed by 8-9 digits
  const saPhoneRegex = /^(\+27|0)[6-8]\d{8}$/;
  return saPhoneRegex.test(phone.replace(/\s/g, ""));
}

/**
 * Format phone number to E.164 format for South Africa
 */
export function formatPhoneToE164(phone: string): string {
  const cleaned = phone.replace(/\s/g, "");

  if (cleaned.startsWith("+27")) {
    return cleaned;
  }

  if (cleaned.startsWith("0")) {
    return `+27${cleaned.slice(1)}`;
  }

  return `+27${cleaned}`;
}

/**
 * Validate South African postal code
 */
export function validateSAPostalCode(postalCode: string): boolean {
  // South African postal codes are 4 digits
  const postalCodeRegex = /^\d{4}$/;
  return postalCodeRegex.test(postalCode);
}

/**
 * South African provinces
 */
export const SA_PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
] as const;

export type SAProvince = (typeof SA_PROVINCES)[number];

/**
 * Check if shipping method conditions are met based on cart subtotal and/or weight
 */
export function areConditionsMet(
  method: ShippingMethod,
  subtotalCents: number,
  weightGrams?: number,
): boolean {
  if (!method.conditions || method.conditions.length === 0) return true;

  return method.conditions.every((condition) => {
    const amount = condition.criteria?.amount ?? 0;

    if (condition.field === "TOTAL_PRICE") {
      switch (condition.operator) {
        case "GREATER_THAN_OR_EQUAL_TO":
          return subtotalCents >= amount;
        case "LESS_THAN_OR_EQUAL_TO":
          return subtotalCents <= amount;
        case "GREATER_THAN":
          return subtotalCents > amount;
        case "LESS_THAN":
          return subtotalCents < amount;
        case "EQUAL_TO":
          return subtotalCents === amount;
        default:
          return true;
      }
    }

    if (condition.field === "WEIGHT_BASED") {
      // If any cart item lacks a weight, the caller passes undefined and we
      // hide the shipping option entirely (fail the condition).
      if (weightGrams === undefined) return false;
      const weight = weightGrams;
      switch (condition.operator) {
        case "GREATER_THAN_OR_EQUAL_TO":
          return weight >= amount;
        case "LESS_THAN_OR_EQUAL_TO":
          return weight <= amount;
        case "GREATER_THAN":
          return weight > amount;
        case "LESS_THAN":
          return weight < amount;
        case "EQUAL_TO":
          return weight === amount;
        default:
          return true;
      }
    }

    return true;
  });
}
